/**
 * @file lazyWithRetry.js
 * @description React.lazy for code-split screens, safe across deploys. Each deploy replaces every hashed JS chunk,
 *   so a tab opened before a deploy can ask for a chunk that no longer exists ("ChunkLoadError"). Instead of
 *   crashing, reload the page once to pick up the new build; a session flag prevents a reload loop, and a second
 *   failure is thrown as usual.
 * @module lib/lazyWithRetry
 */
import { lazy } from "react";

const FLAG = "abacases_chunk_reload";

function isChunkError(err) {
  const msg = String((err && (err.name + " " + err.message)) || "");
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(msg);
}

/** Wrap an import() for React.lazy so a stale chunk triggers one page reload instead of an error screen. */
export function lazyWithRetry(importer) {
  return lazy(() =>
    importer()
      .then((mod) => {
        try { sessionStorage.removeItem(FLAG); } catch (_) {}
        return mod;
      })
      .catch((err) => {
        let reloaded = false;
        try { reloaded = sessionStorage.getItem(FLAG) === "1"; } catch (_) {}
        if (isChunkError(err) && !reloaded) {
          try { sessionStorage.setItem(FLAG, "1"); } catch (_) {}
          window.location.reload();
          return new Promise(() => {}); // the reload replaces the page
        }
        throw err;
      })
  );
}

export { isChunkError };
